<template>
	<CardContext 
		ref="card" 
		:loading="loading" 
		:message="message" 
		:app="app"
		:card="card" 
		:actions="actions" 
		:substitutable="true"
		@init="handleInit" 
		@substitute="handleSubstitute" 
		@action="handleAction"
		@contextmenu.prevent>

		<template #title>
			{{ title }}
		</template>

		<template v-if="showContext" #context>
			<ToolbarImageContext 
				ref="context"
				:isSketch="isSketch"
				@update:tool="handleTool" 
				@rotate="handleRotate" 
				@background="handleBackground"
				@undo="handleUndo" 
				@redo="handleRedo" 
				@reset="handleReset" 
				@download="handleDownload" />
		</template>

		<template v-if="showFlyout" #flyout>
			<ToolbarImageFlyout 
				:isSketch="isSketch"
				:tool="tool" 
				v-model:markerSize="markerSize"
				v-model:eraseSize="eraseSize" 
				v-model:eraseMode="eraseMode" 
				@trigger="handleTrigger" />
		</template>

		<template v-if="showLayer" #layer>
			<ToolbarImageLayer 
				:category="category" 
				:thumbnails="thumbnails" 
				@example="handleExample" />
		</template>

		<template #substitute>
			<div class="card-image-content">
				<div 
					class="card-image-stage" 
					:class="{'single': isSketch, 'multiple': !isSketch, 'is-dragging': stage.drag.active }"
					:style="stageStyle"
					@wheel.prevent="handleStageWheel"
					@pointerdown="handleStagePointerDown"
					@pointermove="handleStagePointerMove"
					@pointerup="handleStagePointerUp"
					@pointercancel="handleStagePointerCancel"
					@contextmenu.prevent>
					<ViewerImageRaw 
						:data="substitute" 
						:animated="false" />
				</div>
			</div>
		</template>

		<template #main>
			<div class="card-image-content">
				<div 
					class="card-image-stage" 
					:class="{'single': isSketch, 'multiple': !isSketch, 'is-dragging': stage.drag.active }"
					:style="stageStyle"
					@wheel.prevent="handleStageWheel"
					@pointerdown="handleStagePointerDown"
					@pointermove="handleStagePointerMove"
					@pointerup="handleStagePointerUp"
					@pointercancel="handleStagePointerCancel"
					@contextmenu.prevent>
					<Composition 
						ref="composition" 
						:isSketch="isSketch"
						:tool="tool" 
						:eraseMode="eraseMode" 
						v-model:markerSize="markerSize"
						v-model:eraseSize="eraseSize" 
						:defaultResolution="defaultResolution"
						:thumbnailResolution="thumbnailResolution"
						:thumbnailAutomatic="isDebugLayer" 
						@tool-change="handleToolChange"
						@layer-change="handleLayerChange"
						@layer-thumbnail="handleLayerThumbnail" />
				</div>
			</div>
		</template>

	</CardContext>
</template>

<script>
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { File } from '@generio-ai/library-legacy/stores/_api'
import ACTION from '@/constants/action.js'
import CARD from '@/constants/card.js'
import { getBase64FromImageUrl } from '@/utils/file.js'
import Composition from '@/components/common/composition/Composition.vue'
import ToolbarImageContext from '@/components/common/toolbar/ToolbarImageContext.vue'
import ToolbarImageFlyout from '@/components/common/toolbar/ToolbarImageFlyout.vue'
import ToolbarImageLayer from '@/components/common/toolbar/ToolbarImageLayer.vue'
import ViewerImageRaw from '@/components/common/viewer/ViewerImageRaw.vue'
import CardContext from '@/components/flow/cards/CardContext.vue'

export default {
	name: 'CardImage',
	components: {
		CardContext,
		ViewerImageRaw,
		Composition,
		ToolbarImageContext,
		ToolbarImageFlyout,
		ToolbarImageLayer
	},
	props: {
		app: {
            type: String,
            required: true
        },
		card: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			loading: false,
			message: '',
			substitute: null,
			ready: false,
			state: null,

			tool: null,
			markerSize: 5,
			eraseSize: 5,
			eraseMode: 'all',
			stage: {
				offset: null,
				position: {
					x: 0,
					y: 0
				},
				drag: {
					active: false,
					pointerId: null,
					startX: 0,
					startY: 0,
					originX: 0,
					originY: 0,
					bounds: null
				},
				scale: 1,
				lastRightDownTime: 0,
				doubleClickThreshold: 350
			},

			empty: {
				image: true,
				marker: true,
				sketch: true
			},

			thumbnails: {
				image: null,
				marker: null,
				sketch: null
			},

			defaultResolution: 1024,
			thumbnailResolution: 196,

			saved: false,
			lastAsset: null
		}
	},
	computed: {
		...mapGetters('flows', [
			'isActiveByIndex',
			'isDebugLayer',
			'getParentCard'
		]),

		isActiveCard() {
			return this.isActiveByIndex(this.app, this.index)
		},

		isCropToolActive() {
			return this.tool === 'crop'
		},

		isStageInteractionLocked() {
			return this.isCropToolActive
		},

		isSketch() {
			return this.card.category === CATEGORY.SKETCH
		},

		isEmpty() {
			return this.empty.image && this.empty.marker && this.empty.sketch	
		},
		isLayerImageEmpty() { 
			return this.empty.image
		},
		isLayerMarkerEmpty() {
			return this.empty.marker
		},
		isLayerSketchEmpty() {
			return this.empty.sketch
		},

		isOnlyLayerImage() {
			return !this.empty.image && this.empty.marker && this.empty.sketch
		},
		isOnlyLayerMarker() {
			return this.empty.image && !this.empty.marker && this.empty.sketch
		},
		isOnlyLayerSketch() {
			return this.empty.image && this.empty.marker && !this.empty.sketch
		},

		isCategoryPhoto() {
			return this.category === CATEGORY.PHOTO
		},
		isCategorySketch() {
			return this.category === CATEGORY.SKETCH
		},
		isCategoryComposite() {
			return this.category === CATEGORY.COMPOSITE
		},

		hasAsset() {
			return this.asset !== null && this.asset !== undefined
		},

		showContext() {
			return this.ready
		},
		showFlyout() {
			return this.ready && (this.tool === 'marker' || this.tool === 'erase' || this.tool === 'crop')
		},
		showLayer() {
			return this.ready && (this.isDebugLayer || (this.isCategorySketch && this.isLayerSketchEmpty))
		},

		title() {
			if (this.isCategorySketch) return 'Sketch your idea.'
			return 'Highlight image area to edit.'
		},
		actions() {
			switch (this.category) 
			{
				case CATEGORY.SKETCH:
					return [
						ACTION.DESCRIBE_IDEA,
					]
				case CATEGORY.PHOTO:
					return [
						ACTION.GENERATE_MODEL,
						ACTION.DESCRIBE_EDIT
					]
				case CATEGORY.COMPOSITE:
					return [
						ACTION.GENERATE_MODEL,
						ACTION.DESCRIBE_EDIT
					]
			}
			return []
		},

		index() {
			return this.card.index
		},
		nextIndex() {
			return this.index + 1
		},
		category() {
			if (this.isSketch) return CATEGORY.SKETCH
			if (this.ready) {
				if (this.isLayerMarkerEmpty) return CATEGORY.PHOTO
				return CATEGORY.COMPOSITE
			}
			return this.card.category
		},
		asset() {
			return this.card.getSelectedOrFirstAsset()
		},

		parentCard() {
			return this.getParentCard(this.app, this.card)
		},
		parentAsset() {
			return this.parentCard?.getSelectedOrFirstAsset()
		},

		activeAsset() {
			if (this.hasAsset) return this.asset
			return this.parentAsset
		},
		activeFile() {
			return this.activeAsset?.files?.['default'] || {}
		},

		stageStyle() {
			const style = {}
			if (this.stage.offset !== null && this.stage.offset !== undefined) {
				style['--stage-offset'] = `${this.stage.offset}px`
			}
			const translate = `translate3d(${this.stage.position.x}px, ${this.stage.position.y}px, 0)`
			const scale = `scale(${this.stage.scale})`
			style.transform = `${translate} ${scale}`
			if (this.stage.drag.active) {
				style.cursor = 'grabbing'
			}
			return style
		}
	},
	watch: {
		tool(newTool, oldTool) {
			if (newTool === 'crop') {
				this.resetStageDrag()
				this.resetStageTransform({ includeOffset: true })
			}
			else if (oldTool === 'crop') {
				this.resetStageDrag()
			}
		}
	},
	methods: {
		...mapActions('dialogs', [
            'open'
        ]),
		...mapActions('notifications', [
			'show'
		]),
		...mapActions('flows', [
			'removeCard',
			'newTransaction',
			'commitFilesAsset',
			'fetchBase',
			'intermediatePostAssets',
			'intermediatePostFile',
			'intermediateRemoveBackground'
		]),
		async handleInit() {
			await this.initComposition()
			this.lastAsset = this.activeAsset
			this.substitute = this.activeFile?.data || null
			this.saved = true
			this.ready = true
		},
		handleSubstitute(isSubstituted) 
		{
			// flag as not ready
			this.ready = false

			if (isSubstituted) 
			{
				// export current state
				this.state = this.$refs.composition?.exportState(true)
				this.ready = false

				// render lightweight static substitute
				switch(this.category) {
					case CATEGORY.PHOTO:
						this.substitute = this.renderImage()
						break
					case CATEGORY.SKETCH:
						this.substitute = this.renderSketch()
						break
					case CATEGORY.COMPOSITE:
						this.substitute = this.renderComposite()
						break
					default:
						this.substitute = null
				}
			}
			else
			{
				this.$nextTick(async () => {
					// if we have a prior serialized state, restore it; else init from asset files
					if (this.state) await this.$refs.composition?.importState(this.state)
					else await this.initComposition()
	
					// clear serialized cache after restore to free memory (optional)
					this.state = null
					this.ready = true
				})
			}
			
		},
		handleAction(action) {
			// update to current category
			this.card.category = this.category

			switch (action.id) 
			{
				case ACTION.GENERATE_MODEL.id:
					// force photo category (could be composite but renderImage creates an image)
					this.card.category = CATEGORY.PHOTO
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitFilesAsset({
							files: [
								new File(ASSETS.FILES.KEY.DEFAULT, this.getLayerImage())
							],
							preload: true
						}),
						taskFetch: this.fetchBase({
							component: CARD.GENERATE_GEOMETRY,
							category: CATEGORY.PHOTO
						})
					})
					break

				case ACTION.DESCRIBE_EDIT.id:
					if (this.category === CATEGORY.COMPOSITE) 
					{
						this.newTransaction({
							app: this.app,
							card: this.card,
							taskCommit: this.commitFilesAsset({
								files: [
									new File(ASSETS.FILES.KEY.DEFAULT, this.getLayerImage()),
									new File(ASSETS.FILES.KEY.MARKER, this.getLayerMarker())
								],
								thumbnail: this.renderThumbnailComposite(),
								preload: true
							}),
							taskFetch: this.fetchBase({
								component: CARD.PROMPT,
								category: this.category,
							})
						})
					}
					else
					{
						this.newTransaction({
							app: this.app,
							card: this.card,
							taskCommit: this.commitFilesAsset({
								files: [
									new File(ASSETS.FILES.KEY.DEFAULT, this.getLayerImage())
								],
								preload: true
							}),
							taskFetch: this.fetchBase({
								component: CARD.PROMPT,
								category: this.category,
							})
						})
					}
					break

				case ACTION.DESCRIBE_IDEA.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitFilesAsset({
							files: [
								new File(ASSETS.FILES.KEY.DEFAULT, this.renderSketch())
							],
							preload: true
						}),
						taskFetch: this.fetchBase({
							component: CARD.PROMPT,
							category: this.category, // sketch
						})
					})
					break
			}
		},
		handleTool(tool) {
			this.tool = tool
		},
		handleTrigger(trigger) {
			const composition = this.$refs.composition
			if (!composition) return
			composition.invokeTrigger(trigger)
		},
		async handleBackground() 
		{
			// inform user
			this.loading = true
			this.message = "removing background"

			// pointer to correct file key
			let fileKey = "render"

			try
			{
				// update a rendered version of the image
				await this.intermediatePostFile({
					asset: this.activeAsset,
					fileData: this.renderImage(),
					fileKey
				})

				// call intermediate action to remove background
				const response = await this.intermediateRemoveBackground({
					app: this.app,
					card: this.card,
					parentCard: this.parentCard,
					asset: this.activeAsset, 
					fileKey: fileKey,
					ignore: true,
					preload: true
				})

				// new created image asset with removed background
				const newAsset = response.assets?.[0]

				// get new image file
				const newData = newAsset?.files?.[ASSETS.FILES.KEY.DEFAULT]?.data

				// ensure image data is valid
				if (!newData) throw new Error("No image data returned from background removal")

				// merge mask into image layer of composition
				await this.$refs.composition?.applyTransparency('image', newData, { 
					invert: true,
					union: true 
				})
			}
			catch(error) 
			{
				console.log(error)
				this.show({
					message: "Unable to remove the background",
					level: LEVELS.ERROR
				})
			}
			finally
			{
				this.loading = false
			}
		},
		handleRotate() {
			this.$refs.composition?.rotate()
		},
		handleUndo() {
			this.$refs.composition?.undo()
		},
		handleRedo() {
			this.$refs.composition?.redo()
		},
		handleReset() {
			this.initComposition()
		},
		async handleDownload() 
		{
			// placeholder for selected asset
			let selectedAsset = null

			// if saved asset is still up-to-date
			if (this.saved && this.lastAsset) {
				selectedAsset = this.lastAsset
			}
			else
			{
				try
				{
					// post file to new asset
					const response = await this.intermediatePostAssets({
						app: this.app,
						card: this.card,
						parentCard: this.parentCard,
						fileData: this.isSketch ? this.renderSketch() : this.getLayerImage(),
						ignore: false,
						preload: true
					})

					// get new created asset
					selectedAsset = response.assets?.[0]
					if (!selectedAsset) throw new Error("No asset returned from intermediate post")

					// store for next time
					this.lastAsset = selectedAsset
					this.saved = true
				}
				catch(error)
				{
					console.log(error)
					this.show({
						message: "Unable to prepare the image for download",
						level: LEVELS.ERROR
					})
					return
				}
			}

			this.open({
				component: 'download',
				payload: {
					asset: selectedAsset
				}
			})
		},

		handleToolChange() {
			const context = this.$refs.context
			if (!context) return
			context.handleToolSketch()
		},

		handleLayerChange(payload) {
			if (this.ready) this.resetAction()
			this.saved = false
			if (!payload) return
			const { key, empty } = payload
			if (!key) return
			this.empty[key] = empty
		},
		handleLayerThumbnail(payload) {
			if (!payload || !payload.key) return
			const { key, dataUrl } = payload
			if (!key) return
			this.thumbnails[key] = dataUrl
		},

		isEditableTarget(target) {
			if (!target) return false
			const tagName = target.tagName ? target.tagName.toUpperCase() : ''
			if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)) return true
			if (target.isContentEditable) return true
			return false
		},

		getAvailableTools() {
			if (!this.ready) return []
			if (this.isSketch) return ['sketch', 'erase', 'crop']
			return ['marker', 'erase', 'crop']
		},

		applyToolShortcut(toolKey) {
			const context = this.$refs.context
			const methodMap = {
				sketch: 'handleToolSketch',
				marker: 'handleToolMarker',
				erase: 'handleToolErase',
				crop: 'handleToolCrop'
			}
			const methodName = methodMap[toolKey]
			if (context && typeof context?.[methodName] === 'function') {
				context[methodName]()
			}
			else {
				this.handleTool(toolKey)
			}
		},

		cycleTool(direction = 1) {
			const tools = this.getAvailableTools()
			if (tools.length === 0) return
			const currentIndex = tools.indexOf(this.tool)
			const nextIndex = currentIndex === -1
				? 0
				: (currentIndex + direction + tools.length) % tools.length
			this.applyToolShortcut(tools[nextIndex] ?? tools[0])
		},

		triggerResetShortcut() {
			const context = this.$refs.context
			if (context && typeof context.handleReset === 'function') {
				context.handleReset()
			}
			else {
				this.handleReset()
			}
		},

		handleKeyboardShortcuts(event) {
			if (!this.ready || !this.isActiveCard) return
			if (event.defaultPrevented) return
			if (this.isEditableTarget(event.target)) return

			const key = event.key
			const hasCtrlOrMeta = event.ctrlKey || event.metaKey

			if (hasCtrlOrMeta) {
				switch (key.toLowerCase()) {
					case 'z':
						event.preventDefault()
						if (event.shiftKey) this.handleRedo()
						else this.handleUndo()
						return
					case 'y':
						event.preventDefault()
						this.handleRedo()
						return
				}
			}

			if (key === 'Delete') {
				event.preventDefault()
				this.triggerResetShortcut()
				return
			}

			if (key === 'Tab') {
				event.preventDefault()
				const direction = event.shiftKey ? -1 : 1
				this.cycleTool(direction)
			}
		},

		handleExample(example) {
			getBase64FromImageUrl(example.url)
			.then(response => {
				this.$refs.composition?.updateSketch(response)
				this.card.prompt.user = example.prompt
				this.card.prompt.augmentation = example.augmentation
			})
		},

		async initComposition() {
			const files = this.activeAsset?.files || {}
			switch (this.card.category) {
				case CATEGORY.PHOTO:
					await this.$refs.composition?.init(
						files[ASSETS.FILES.KEY.DEFAULT]?.data,
						null, 
						null
					)
					break
				case CATEGORY.SKETCH:
					// image, legacyMask, marker, sketch
					await this.$refs.composition?.init(
						null,
						null,
						files[ASSETS.FILES.KEY.DEFAULT]?.data
					)
					break
				case CATEGORY.COMPOSITE:
					await this.$refs.composition?.init(
						files[ASSETS.FILES.KEY.DEFAULT]?.data,
						files["marker"]?.data || files["mask"]?.data, // mask for backwards compatibility
						null
					)
					break
			}
		},

		resetAction() {
			this.$refs.card?.reset()
			this.removeCard({
				app: this.app,
				index: this.nextIndex
			})
		},

		renderImage() {
			return this.$refs.composition?.renderImage(this.defaultResolution)
		},
		renderMarker() {
			return this.$refs.composition?.renderMarker(this.defaultResolution)
		},
		renderSketch() {
			return this.$refs.composition?.renderSketch(this.defaultResolution)
		},

		renderComposite() {
			return this.$refs.composition?.renderComposite(this.defaultResolution)
		},
		renderThumbnailComposite() {
			return this.$refs.composition?.renderComposite(this.thumbnailResolution, true)
		},

		getLayerImage() {
			return this.$refs.composition?.getLayerImage()
		},
		getLayerMarker() {
			return this.$refs.composition?.getLayerMarker()
		},
		getLayerSketch() {
			return this.$refs.composition?.getLayerSketch()
		},
		handleStageWheel(event) {
			if (this.isStageInteractionLocked) return
			const step = event.deltaY > 0 ? 10 : -10
			if (step === 0) return
			const baseOffset = this.stage.offset ?? this.computeStageOffsetBase()
			const nextOffset = this.clampStageOffset(baseOffset + (step * 8))
			this.stage.offset = nextOffset
			this.$nextTick(() => {
				const composition = this.$refs.composition
				if (composition && typeof composition.updateCursorFromEvent === 'function') {
					composition.updateCursorFromEvent(event)
				}
			})
		},
		handleStagePointerDown(event) {
			if (this.isStageInteractionLocked) return
			if (event.pointerType === 'touch') return
			if (event.button !== 2) return
			const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
				? performance.now()
				: Date.now()
			const elapsed = now - this.stage.lastRightDownTime
			const isDoubleClick = elapsed > 0 && elapsed <= this.stage.doubleClickThreshold
			this.stage.lastRightDownTime = now
			if (isDoubleClick) {
				this.handleStageRightDoubleClick(event)
				return
			}
			event.preventDefault()
			event.stopPropagation()
			const stage = event.currentTarget
			if (stage?.setPointerCapture) {
				stage.setPointerCapture(event.pointerId)
			}
			this.stage.drag.bounds = this.computeStageDragBounds(stage)
			const clampedOrigin = this.clampStagePosition(
				this.stage.position.x,
				this.stage.position.y,
				this.stage.drag.bounds
			)
			if (clampedOrigin.x !== this.stage.position.x || clampedOrigin.y !== this.stage.position.y) {
				this.stage.position.x = clampedOrigin.x
				this.stage.position.y = clampedOrigin.y
			}
			this.stage.drag.active = true
			this.stage.drag.pointerId = event.pointerId
			this.stage.drag.startX = event.clientX
			this.stage.drag.startY = event.clientY
			this.stage.drag.originX = this.stage.position.x
			this.stage.drag.originY = this.stage.position.y
		},
		handleStagePointerMove(event) {
			if (this.isStageInteractionLocked) return
			if (event.pointerType === 'touch') return
			if (!this.stage.drag.active || event.pointerId !== this.stage.drag.pointerId) return
			if ((event.buttons & 2) === 0) {
				this.handleStagePointerUp(event)
				return
			}
			const deltaX = event.clientX - this.stage.drag.startX
			const deltaY = event.clientY - this.stage.drag.startY
			const nextX = this.stage.drag.originX + deltaX
			const nextY = this.stage.drag.originY + deltaY
			const clamped = this.clampStagePosition(nextX, nextY)
			this.stage.position.x = clamped.x
			this.stage.position.y = clamped.y
		},
		handleStagePointerUp(event) {
			if (this.isStageInteractionLocked) return
			if (event.pointerType === 'touch') return
			if (!this.stage.drag.active || event.pointerId !== this.stage.drag.pointerId) return
			const stage = event.currentTarget
			if (stage?.releasePointerCapture) {
				stage.releasePointerCapture(event.pointerId)
			}
			this.resetStageDrag()
		},
		handleStagePointerCancel(event) {
			if (this.isStageInteractionLocked) return
			if (event.pointerType === 'touch') return
			if (!this.stage.drag.active || event.pointerId !== this.stage.drag.pointerId) return
			const stage = event.currentTarget
			if (stage?.releasePointerCapture) {
				stage.releasePointerCapture(event.pointerId)
			}
			this.resetStageDrag()
		},
		handleStageRightDoubleClick(event) {
			if (this.isStageInteractionLocked) return
			event.preventDefault()
			event.stopPropagation()
			const stage = event.currentTarget
			if (stage?.releasePointerCapture) {
				try {
					stage.releasePointerCapture(event.pointerId)
				}
				catch {
					// ignore if pointer was not captured
				}
			}
			this.stage.offset = this.computeStageOffsetBase()
			this.stage.position.x = 0
			this.stage.position.y = 0
			this.stage.scale = 1
			this.stage.lastRightDownTime = 0
			this.resetStageDrag()
		},
		resetStageDrag() {
			this.stage.drag.active = false
			this.stage.drag.pointerId = null
			this.stage.drag.startX = 0
			this.stage.drag.startY = 0
			this.stage.drag.originX = this.stage.position.x
			this.stage.drag.originY = this.stage.position.y
			this.stage.drag.bounds = null
		},
		resetStageTransform(options = {}) {
			const { includeOffset = false } = options
			this.stage.position.x = 0
			this.stage.position.y = 0
			this.stage.scale = 1
			if (includeOffset) {
				this.stage.offset = this.computeStageOffsetBase()
			}
		},
		computeStageDragBounds(stage, scale = this.stage.scale) {
			const container = stage?.parentElement
			const rect = container?.getBoundingClientRect()
			if (!rect) {
				return {
					minX: -Infinity,
					maxX: Infinity,
					minY: -Infinity,
					maxY: Infinity
				}
			}
			const halfWidth = rect.width / 2
			const halfHeight = rect.height / 2
			const extraWidth = (rect.width * Math.max(scale - 1, 0)) / 2
			const extraHeight = (rect.height * Math.max(scale - 1, 0)) / 2
			return {
				minX: -(halfWidth + extraWidth),
				maxX: halfWidth + extraWidth,
				minY: -(halfHeight + extraHeight),
				maxY: halfHeight + extraHeight
			}
		},
		clampStagePosition(x, y, bounds = this.stage.drag.bounds) {
			if (!bounds) {
				return { x, y }
			}
			const clampedX = Math.min(bounds.maxX, Math.max(bounds.minX, x))
			const clampedY = Math.min(bounds.maxY, Math.max(bounds.minY, y))
			return {
				x: clampedX,
				y: clampedY
			}
		},
		clampStageOffset(value) {
			const min = -1000
			const max = 100
			return Math.min(max, Math.max(min, value))
		},
		computeStageOffsetBase() {
			if (typeof window === 'undefined') return 20
			return window.innerWidth <= 600 ? 110 : 20
		}
	},
	mounted() {
		window.addEventListener('keydown', this.handleKeyboardShortcuts)
		this.stage.offset = this.computeStageOffsetBase()
	},
	beforeUnmount() {
		window.removeEventListener('keydown', this.handleKeyboardShortcuts)
	}
}
</script>

<style scoped>
.card-image-content {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	overflow: hidden;
}

.card-image-stage {
	--stage-offset-default: 20px;
	position: relative;
	display: block;
	min-height: 0;
	max-height: calc(100% - var(--stage-offset, var(--stage-offset-default)));
	height: calc(100% - var(--stage-offset, var(--stage-offset-default)));
	aspect-ratio: 1 / 1;
	box-shadow: 0px 8px 16px 0 rgba(var(--v-theme-shadow1), 1);
	touch-action: none;
}

.card-image-stage.is-dragging {
	cursor: grabbing;
}

.card-image-stage.single {
	background: rgb(var(--v-theme-white));
}

.card-image-stage.multiple {
	background: repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50% / 6.25% 6.25%;
}

@media (max-width: 600px) {
	.card-image-content {
		align-items: flex-end;
	}
	.card-image-stage {
		margin-bottom: 10px;
	}
}
</style>